import { useGetModulesQuery } from 'store/services/core/modules';
import { useImportFunctionMutation } from 'store/services/helios/functionsService';
import type { ModalsType } from 'types';
import type { InferType } from 'yup';

import { useTranslation } from 'react-i18next';
import { TextField } from 'components/mui/common/TextField';
import { Box, Collapse, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { BooleanWrapper, FormWrapper, SectionTitle } from './FunctionModals.styles';
import { useForm, Controller, useWatch } from 'react-hook-form';
import { ActionModal } from 'components/mui/common/ActionModal';
import { Switch } from 'components/mui/common/Switch';
import { LookupField } from 'components/mui/common/LookupField';
import { SelectDatasourceModal } from 'components/modals/SelectHeliosDataSourceModal';
import { SelectTemplateModuleModal } from 'components/admin/helios/HeliosTemplates/Sections/SettingsSection/SelectTemplateModuleModal';
import { useImportFunctionTemplateModalSchema } from 'utils/validations/helios/useHeliosFunctionsSchema';
import { yupResolver } from '@hookform/resolvers/yup';

import { LoadingContainer } from 'components/common/LoadingContainer';
import { useCallback, useMemo, useState } from 'react';
import { toast } from 'react-toastify';

const defaultValues = {
  functionName: '',
  heliosFunctionId: undefined,
  isCommon: true,
  dataSourceId: '',
  dataSourceName: '',
  importCodeListTemplates: true,
  moduleId: '',
};

export type ImportFunctionModalSchema = InferType<ReturnType<typeof useImportFunctionTemplateModalSchema>>;

const TRANSLATE_PATH = 'helios.functions.importFunctionModal';

export const ImportFunctionModal: React.FC<ModalsType> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const [isDatasourcePickerOpen, setIsDatasourcePickerOpen] = useState(false);
  const [isModulePickerOpen, setIsModulePickerOpen] = useState(false);
  const importTemplateModalSchema = useImportFunctionTemplateModalSchema();

  const { data: modulesData, isLoading: isModulesLoading } = useGetModulesQuery(
    {
      page: 1,
      pageSize: null,
      search: '',
      orderBy: 'id',
      orderDesc: false,
    },
    { skip: !open }
  );
  const [importFunction] = useImportFunctionMutation();

  const heliosModules = useMemo(() => modulesData?.items.filter((item) => item.isHeliosVisible) ?? [], [modulesData?.items]);

  const {
    control,
    reset,
    setValue,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<ImportFunctionModalSchema>({
    defaultValues,
    resolver: yupResolver(importTemplateModalSchema),
  });

  const [importCodeListTemplates, dataSourceId, moduleId, dataSourceName] = useWatch({
    control,
    name: ['importCodeListTemplates', 'dataSourceId', 'moduleId', 'dataSourceName'],
  });

  const handleClose = useCallback(() => {
    reset();
    setIsDatasourcePickerOpen(false);
    setIsModulePickerOpen(false);
    onClose();
  }, [reset, onClose]);

  const onSubmit = useCallback(
    async (values: ImportFunctionModalSchema) => {
      try {
        const res = await importFunction({
          functionName: values.functionName,
          heliosFunctionId: values.heliosFunctionId,
          dataSourceId: values.dataSourceId,
          isCommon: Boolean(values.isCommon),
          importCodeListTemplates: Boolean(values.importCodeListTemplates),
          moduleId: values.moduleId || undefined,
        });
        if (!('error' in res)) {
          toast.success(t('helios.functions.toasts.success.import'));
          handleClose();
        } else {
          toast.error(t('helios.functions.toasts.errors.import'));
        }
      } catch (error) {
        toast.error(t('helios.functions.toasts.errors.import'));
        // eslint-disable-next-line no-console
        console.error(error);
      }
    },
    [importFunction, handleClose, t]
  );

  return (
    <ActionModal
      open={open}
      title={t(`${TRANSLATE_PATH}.title`)}
      onClose={handleClose}
      onCancel={handleClose}
      onSubmit={handleSubmit(onSubmit)}
      isSubmitting={isSubmitting}
      disableBackdropClick={isSubmitting}
      submitIcon={{
        icon: <AddIcon />,
        position: 'start',
      }}
      cancelText={t(`${TRANSLATE_PATH}.buttons.cancel`)}
      submitText={t(`${TRANSLATE_PATH}.buttons.add`)}>
      <FormWrapper>
        <SectionTitle variant="caption">{t(`${TRANSLATE_PATH}.fields.sections.basicInfo`)}</SectionTitle>
        <Box sx={{ gridColumn: '1 / -1' }}>
          <Controller
            name="dataSourceId"
            control={control}
            render={({ fieldState: { error } }) => (
              <LookupField
                label={t(`${TRANSLATE_PATH}.fields.dataSource`)}
                value={dataSourceName}
                error={Boolean(error)}
                helperText={error?.message}
                fullWidth
                data-testid="admin-helios-import-function-data-source-field"
                onClick={() => setIsDatasourcePickerOpen(true)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    setIsDatasourcePickerOpen(true);
                  }
                }}
              />
            )}
          />
        </Box>

        {isDatasourcePickerOpen && (
          <SelectDatasourceModal
            open
            onClose={() => setIsDatasourcePickerOpen(false)}
            title={t(`${TRANSLATE_PATH}.fields.dataSource`)}
            defaultSelectedDatasource={dataSourceId}
            onConfirm={(selectedDatasource, selectedName) => {
              setValue('dataSourceId', selectedDatasource, { shouldDirty: true, shouldValidate: true });
              setValue('dataSourceName', selectedName ?? '');
              setIsDatasourcePickerOpen(false);
            }}
          />
        )}

        <Controller
          name="functionName"
          control={control}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              error={fieldState.invalid}
              helperText={fieldState.error?.message}
              fullWidth
              label={t(`${TRANSLATE_PATH}.fields.name`)}
            />
          )}
        />
        <Controller
          name="heliosFunctionId"
          control={control}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              type="number"
              error={fieldState.invalid}
              helperText={fieldState.error?.message}
              fullWidth
              label={t(`${TRANSLATE_PATH}.fields.id`)}
            />
          )}
        />
        <SectionTitle variant="caption">{t(`${TRANSLATE_PATH}.fields.sections.importSettings`)}</SectionTitle>
        <BooleanWrapper>
          <Controller
            name="isCommon"
            control={control}
            render={({ field: { value, ref, name, onChange } }) => (
              <Switch
                checked={Boolean(value)}
                onChange={(_e, checked) => onChange(checked)}
                slotProps={{ input: { ref } }}
                name={name}
                label={t(`${TRANSLATE_PATH}.fields.isCommon`)}
              />
            )}
          />
        </BooleanWrapper>
        <Box
          sx={{
            gridColumn: '1 / -1',
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            p: 2,
          }}>
          <Controller
            name="importCodeListTemplates"
            control={control}
            render={({ field: { value, ref, name, onChange } }) => (
              <Switch
                checked={Boolean(value)}
                onChange={(_e, checked) => onChange(checked)}
                slotProps={{ input: { ref } }}
                name={name}
                label={t(`${TRANSLATE_PATH}.fields.importCodeListTemplates`)}
              />
            )}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75, ml: 1 }}>
            {t(`${TRANSLATE_PATH}.fields.importCodeListTemplatesHelp`)}
          </Typography>
          <Collapse in={importCodeListTemplates}>
            <Box sx={{ mt: 2 }}>
              <LoadingContainer loading={isModulesLoading}>
                <Controller
                  name="moduleId"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <LookupField
                      label={t(`${TRANSLATE_PATH}.fields.module`)}
                      value={heliosModules.find((item) => item.id === field.value)?.name ?? ''}
                      error={Boolean(error)}
                      helperText={error?.message}
                      fullWidth
                      disabled={isModulesLoading}
                      data-testid="admin-helios-import-function-module-field"
                      onClick={() => setIsModulePickerOpen(true)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          setIsModulePickerOpen(true);
                        }
                      }}
                    />
                  )}
                />
              </LoadingContainer>
            </Box>
          </Collapse>
        </Box>
        {isModulePickerOpen && (
          <SelectTemplateModuleModal
            open
            defaultSelectedId={moduleId ?? ''}
            defaultSelectedName={heliosModules.find((item) => item.id === moduleId)?.name ?? ''}
            onClose={() => setIsModulePickerOpen(false)}
            onConfirm={({ id: selectedModuleId }) => {
              setValue('moduleId', selectedModuleId, { shouldDirty: true, shouldValidate: true });
              setIsModulePickerOpen(false);
            }}
          />
        )}
      </FormWrapper>
    </ActionModal>
  );
};
